// Session JWT captured from the login response — the Safari-proof twin of
// the httpOnly cookie. sessionStorage: per-tab, gone when the tab closes,
// same lifetime class as the old shared-secret header this replaces.
const SESSION_TOKEN_KEY = 'editorSessionToken';

export function setSessionToken(token: string | undefined) {
    if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    else sessionStorage.removeItem(SESSION_TOKEN_KEY);
}

// For the rare caller that must use raw fetch (binary responses) and still
// needs the same bearer fallback sendRequest applies — without this, such a
// call authenticates only via the cookie, which cross-site setups (e.g.
// localhost dev against the remote API, Safari ITP) never send.
export function getSessionToken(): string | undefined {
    return sessionStorage.getItem(SESSION_TOKEN_KEY) ?? undefined;
}

export async function get(url: string, accessToken: string | undefined) {
    return await sendRequest(url, 'GET', undefined, accessToken);
}

export async function post(url: string, payload: string, accessToken: string | undefined) {
    return await sendRequest(url, 'POST', payload, accessToken);
}

export async function postFile(url: string, payload: FormData, accessToken: string | undefined) {
    return await sendRequest(url, 'POST', payload, accessToken);
}

export async function deleteRequest(url: string, payload: string, accessToken: string | undefined) {
    return await sendRequest(url, 'DELETE', payload, accessToken);
}

async function sendRequest(
    url: string,
    method: string,
    payload: string | FormData | undefined,
    accessToken: string | undefined,
) {
    try {
        let contentType: string | undefined = 'application/json';
        if (payload instanceof FormData) {
            contentType = undefined;
        }

        let headers: HeadersInit = {};
        // Explicit accessToken wins; otherwise fall back to the session JWT
        // captured at login. The httpOnly cookie remains the primary auth,
        // but Safari never sends it cross-site (ITP blocks third-party
        // cookies), so the bearer echo is what keeps Safari sessions alive.
        const bearer = accessToken ?? sessionStorage.getItem(SESSION_TOKEN_KEY) ?? undefined;
        if (bearer !== undefined) {
            headers['Authorization'] = 'Bearer ' + bearer;
        }

        if (contentType !== undefined) {
            headers['Content-Type'] = contentType;
        }

        const response = await fetch(url, {
            method: method,
            headers: headers,
            body: payload,
            credentials: 'include', // send/receive the editor session cookie
        });

        const ok = response.status >= 200 && response.status < 300;
        let data: any;
        try {
            data = await response.json();
        } catch (_) {
            data = null;
        }

        console.log('<<', method, response.status, url, data);
        return {
            success: ok,
            status: response.status,
            data: data,
        };
    } catch (e) {
        return {
            success: false,
            status: 0, // network / CORS failure — no HTTP response
            data: {},
        };
    }
}